import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  adminGetPedidos, adminMarcarPedidoPagado, adminCancelarPedido,
  adminMarcarPedidoEnArmado, adminMarcarPedidoEnRuta, adminMarcarPedidoEntregado,
  adminEditarEnvioPedido, adminGetBarrios,
  adminCambiarEstadoPedido, adminReabrirPedidoEntregado, adminEditarPedido, adminCrearPedidoManual,
} from '../../lib/portalClienteApi'
import { getCuentaCobro } from '../../lib/cobrosApi'
import { formatearRut } from '../../lib/rutUY'
import { labelCortoVentana, opcionesVentana } from '../../lib/ventanasHorarias'
import { supabase } from '../../lib/supabase'
import AliadoChip from '../../components/AliadoChip'

// helper local que tolera null/undefined
function fmtRut(rut) { return rut ? formatearRut(rut) : '—' }
const ceil = (n) => Math.ceil(Number(n || 0))

const ESTADO_PILL = {
  borrador:        { bg: '#f1f5f9', color: '#64748b', label: 'Borrador' },
  pendiente_pago:  { bg: '#fef3c7', color: '#92400e', label: 'Pendiente pago' },
  pagado:          { bg: '#dbeafe', color: '#1e40af', label: 'Pagado' },
  en_armado:       { bg: '#e0e7ff', color: '#3730a3', label: 'En armado' },
  en_ruta:         { bg: '#cffafe', color: '#0e7490', label: 'En ruta' },
  entregado:       { bg: '#dcfce7', color: '#15803d', label: 'Entregado' },
  cancelado:       { bg: '#fee2e2', color: '#991b1b', label: 'Cancelado' },
  rechazado:       { bg: '#fee2e2', color: '#991b1b', label: 'Rechazado' },
}

// Estados a los que se puede mover con el selector libre (entregado va por su propia acción)
const ESTADOS_LIBRES = ['borrador', 'pendiente_pago', 'pagado', 'en_armado', 'en_ruta', 'cancelado', 'rechazado']
// Estados válidos como destino al reabrir un entregado
const ESTADOS_REABRIR = ['pendiente_pago', 'pagado', 'en_armado', 'en_ruta']

const TIPO_LABEL = {
  compra_unica:           '📦 Kit único',
  productos_sueltos:      '🛍️ Tienda',
  adicional_suscripcion:  '➕ Extra suscriptor',
  suscripcion:            '🔄 Suscripción',
}

const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'

// Liberación de fondos MP: cuándo la plata cobrada pasa de "retenida" a disponible en la caja de MP.
function libDateCorta(s) { if (!s) return ''; const p = String(s).slice(0, 10).split('-'); return `${p[2]}/${p[1]}` }
function libDateLarga(s) { return s ? new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString('es-UY', { day: '2-digit', month: 'long' }) : '' }
function libInfo(reg) {
  if (!reg) return null
  const real = reg.fecha_liberacion_real, est = reg.fecha_liberacion_estimada
  if (!real && !est) return null
  const monto = Number(reg.monto_neto || 0)
  if (real) return { estado: 'liberado', fecha: real, monto, aprox: false }
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  if (new Date(est + 'T00:00:00') > hoy) return { estado: 'retenido', fecha: est, monto, aprox: false }
  return { estado: 'liberado', fecha: est, monto, aprox: true }
}

// Detecta el canal del pedido para el filtro (client-side)
function detectarCanal(p) {
  if (p.empresa_id) return 'b2b'
  const notas = (p.notas || '').toLowerCase()
  if (notas.includes('venta manual')) return 'manual'
  if (notas.includes('mercadolibre') || notas.includes('mercado libre')) return 'ml'
  return 'tienda'
}

const CANAL_LABEL = {
  todos: 'Todos', tienda: '🏪 Tienda', ml: '🛒 ML', manual: '🛍 Manual', b2b: '🏢 B2B',
}

export default function AdminPedidos() {
  const location = useLocation()
  const [pedidos, setPedidos] = useState([])   // TODOS los pedidos cargados
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('todos')
  const [canal, setCanal] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [verTests, setVerTests] = useState(false)   // oculta pedidos es_prueba por defecto
  const [accion, setAccion] = useState(null)
  const [estadoModal, setEstadoModal] = useState(null)
  const [editarModal, setEditarModal] = useState(null)
  const [regaloModal, setRegaloModal] = useState(null)
  const [crearOpen, setCrearOpen] = useState(false)
  const [expandidos, setExpandidos] = useState(new Set())
  const [esSuper, setEsSuper] = useState(false)
  const [highlightId, setHighlightId] = useState(null) // pedido a resaltar (viene por ?highlight=)
  const [libMap, setLibMap] = useState({}) // mp_payment_id → registro de liberación MP
  const rowsRef = useRef({}) // ref map: pedido.id → DOM element

  useEffect(() => { supabase.rpc('is_superadmin').then(({ data }) => setEsSuper(!!data)) }, [])

  // Leer ?highlight=<uuid> de la URL una sola vez al montar
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const id = params.get('highlight')
    if (id) setHighlightId(id)
  }, [location.search])

  async function cargar() {
    setLoading(true)
    // Traemos SIEMPRE todo (limit alto). Filtramos client-side para tener counts reales
    // en todos los tabs y buscador que atraviesa todos los estados.
    const data = await adminGetPedidos({ limit: 200 })
    const idsEmpresa = [...new Set((data || []).map(p => p.empresa_id).filter(Boolean))]
    const idsAliado  = [...new Set((data || []).map(p => p.vendedor_referido_id).filter(Boolean))]
    let list = data || []
    if (idsEmpresa.length) {
      const { data: emps } = await supabase
        .from('empresas').select('id, razon_social, rut, email_facturacion').in('id', idsEmpresa)
      const mapa = Object.fromEntries((emps || []).map(e => [e.id, e]))
      list = list.map(p => (p.empresa_id && mapa[p.empresa_id])
        ? { ...p, _empresa: mapa[p.empresa_id] } : p)
    }
    if (idsAliado.length) {
      const { data: alis } = await supabase
        .from('porteros').select('id, slug, nombre_completo').in('id', idsAliado)
      const mapaA = Object.fromEntries((alis || []).map(a => [a.id, a]))
      list = list.map(p => (p.vendedor_referido_id && mapaA[p.vendedor_referido_id])
        ? { ...p, _aliado: mapaA[p.vendedor_referido_id] } : p)
    }
    // Liberación MP: para los pedidos con pago MP, traemos su registro (retenido/liberado).
    const payIds = [...new Set(list.map(p => p.mp_payment_id).filter(Boolean))]
    if (payIds.length) {
      const { data: pagos } = await supabase
        .from('mp_pagos_registro')
        .select('mp_payment_id, fecha_liberacion_estimada, fecha_liberacion_real, monto_neto')
        .in('mp_payment_id', payIds)
      setLibMap(Object.fromEntries((pagos || []).map(r => [r.mp_payment_id, r])))
    } else {
      setLibMap({})
    }
    // Sort: created_at DESC (más nuevos arriba)
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    setPedidos(list)
    setLoading(false)
  }

  useEffect(() => { cargar() }, [])

  // Base visible: oculta pedidos de prueba (es_prueba) salvo toggle
  const pedidosVisibles = useMemo(() => verTests ? pedidos : pedidos.filter(p => !p.es_prueba), [pedidos, verTests])
  const testsOcultos = useMemo(() => pedidos.filter(p => p.es_prueba).length, [pedidos])

  // Counts SIEMPRE sobre el total visible (no sobre el filtrado)
  const counts = useMemo(() =>
    pedidosVisibles.reduce((acc, p) => { acc[p.estado] = (acc[p.estado] || 0) + 1; return acc }, {}),
    [pedidosVisibles])

  // Vista filtrada: estado + canal + búsqueda
  const pedidosVista = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    return pedidosVisibles.filter(p => {
      if (filtro !== 'todos' && p.estado !== filtro) return false
      if (canal !== 'todos' && detectarCanal(p) !== canal) return false
      if (q) {
        const hay = [
          p.numero_orden, p.comprador_nombre, p.comprador_email, p.comprador_telefono,
          p.comprador_documento, p.facturacion_razon_social, p.facturacion_rut,
          p._empresa?.razon_social, p._empresa?.rut, p.entrega_direccion,
        ].filter(Boolean).map(String).map(s => s.toLowerCase()).join(' | ')
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [pedidosVisibles, filtro, canal, busqueda])

  // Highlight: al terminar el sort/render, expandir el pedido resaltado + scroll + limpiar el highlight tras 3s
  useEffect(() => {
    if (!highlightId || loading) return
    const existe = pedidos.some(p => p.id === highlightId)
    if (!existe) return
    // Expandir la card resaltada
    setExpandidos(prev => {
      const s = new Set(prev); s.add(highlightId); return s
    })
    // Scroll + limpiar el flash tras 3s
    const t1 = setTimeout(() => {
      const el = rowsRef.current[highlightId]
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
    const t2 = setTimeout(() => setHighlightId(null), 3000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [highlightId, loading, pedidos])

  const refrescar = () => { setAccion(null); setEstadoModal(null); setEditarModal(null); setRegaloModal(null); setCrearOpen(false); cargar() }

  return (
    <AdminLayout>
      <div className="page">
        <div className="ap-head-row">
          <div>
            <h1>📦 Pedidos one-shot</h1>
            <p className="muted">Pedidos de Kit suelto y La Tienda. Confirmá pagos, movelos de estado, editalos o cargá uno a mano.</p>
          </div>
          <button className="ap-btn-nuevo" onClick={() => setCrearOpen(true)}>+ Nuevo one-shot</button>
        </div>

        {/* Buscador */}
        <input
          type="search"
          className="ap-search"
          placeholder="🔎 Buscar por número, cliente, teléfono, CI/RUT, email, dirección…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />

        {/* Filtros de estado */}
        <div className="ap-filtros">
          {[
            { v: 'todos',           label: 'Todos',              c: pedidosVisibles.length },
            { v: 'borrador',        label: '📝 Borrador',        c: counts.borrador || 0 },
            { v: 'pendiente_pago',  label: '⏳ Pendiente pago',  c: counts.pendiente_pago || 0 },
            { v: 'pagado',          label: '💰 Pagado',          c: counts.pagado || 0 },
            { v: 'en_armado',       label: '📦 En armado',       c: counts.en_armado || 0 },
            { v: 'en_ruta',         label: '🚐 En ruta',         c: counts.en_ruta || 0 },
            { v: 'entregado',       label: '✅ Entregado',       c: counts.entregado || 0 },
            { v: 'cancelado',       label: '❌ Cancelado',       c: counts.cancelado || 0 },
          ].filter(o => o.v === 'todos' || o.c > 0).map(o => (
            <button key={o.v}
              className={`ap-filtro ${filtro === o.v ? 'is-active' : ''}`}
              onClick={() => setFiltro(o.v)}>
              {o.label} <em>{o.c}</em>
            </button>
          ))}
        </div>

        {/* Filtros de canal */}
        <div className="ap-filtros ap-filtros-canal">
          {['todos', 'tienda', 'ml', 'manual', 'b2b'].map(c => (
            <button key={c}
              className={`ap-filtro ap-filtro-canal ${canal === c ? 'is-active' : ''}`}
              onClick={() => setCanal(c)}>
              {CANAL_LABEL[c]}
            </button>
          ))}
        </div>

        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && (
          <div className="ap-list-info">
            Mostrando <strong>{pedidosVista.length}</strong> de {pedidosVisibles.length} pedidos
            {(busqueda || canal !== 'todos' || filtro !== 'todos') && (
              <button className="ap-link" onClick={() => { setBusqueda(''); setCanal('todos'); setFiltro('todos') }}>
                × Limpiar filtros
              </button>
            )}
            {testsOcultos > 0 && (
              <button className="ap-link" onClick={() => setVerTests(v => !v)}>
                {verTests ? `× Ocultar ${testsOcultos} de prueba` : `Ver ${testsOcultos} de prueba`}
              </button>
            )}
          </div>
        )}

        {!loading && pedidosVista.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🌱</div>
            <h3>{pedidos.length === 0 ? 'Sin pedidos' : 'Sin resultados con estos filtros'}</h3>
            <p>{pedidos.length === 0
              ? 'Acá van a aparecer los pedidos one-shot. También podés cargar uno a mano con "+ Nuevo one-shot".'
              : 'Probá limpiar los filtros o cambiar la búsqueda.'}</p>
          </div>
        )}

        <div className="ap-list">
          {pedidosVista.map(p => {
            const pill = ESTADO_PILL[p.estado] || ESTADO_PILL.pendiente_pago
            const lib = libInfo(libMap[p.mp_payment_id])
            const items = Array.isArray(p.items) ? p.items : []
            const cantProductos = items.reduce((s, it) => s + Number(it.cantidad || 1), 0)
            const resumen = p.kit_codigo
              ? `Kit ${p.kit_codigo.replace(/_/g, ' ')}`
              : items.length > 0
                ? `${cantProductos} producto${cantProductos === 1 ? '' : 's'}`
                : '—'
            const isOpen = expandidos.has(p.id)
            const isHighlighted = highlightId === p.id
            const esTerminal = ['cancelado', 'entregado', 'rechazado'].includes(p.estado)
            const toggleExpand = () => {
              setExpandidos(prev => {
                const nuevo = new Set(prev)
                if (nuevo.has(p.id)) nuevo.delete(p.id); else nuevo.add(p.id)
                return nuevo
              })
            }
            const copiar = (txt) => { if (txt) navigator.clipboard?.writeText(txt) }
            return (
              <div key={p.id}
                   ref={el => { if (el) rowsRef.current[p.id] = el }}
                   className={`ap-pedido ${isHighlighted ? 'is-highlighted' : ''} ${isOpen ? 'is-open' : 'is-collapsed'}`}>
                <div className="ap-pedido-head" onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                  <div className="ap-pedido-head-left">
                    <span className="ap-expand-caret">{isOpen ? '▾' : '▸'}</span>
                    <div className="ap-pedido-num">{p.numero_orden}</div>
                    <span className="ap-pedido-cliente-compact">{p.comprador_nombre}</span>
                    <span className="ap-pedido-total-compact">${Number(p.monto_total).toLocaleString('es-UY')}</span>
                    <span className="ap-pedido-canal">{CANAL_LABEL[detectarCanal(p)]}</span>
                    {(p._aliado || p.referral_slug) && (
                      <AliadoChip
                        slug={p._aliado?.slug || p.referral_slug}
                        nombre={p._aliado?.nombre_completo}
                      />
                    )}
                  </div>
                  <span className="ap-pill" style={{ background: pill.bg, color: pill.color }}>{pill.label}</span>
                </div>
                {isOpen && (
                <div className="ap-pedido-grid">
                  <div>
                    <div className="ap-tag">Cliente</div>
                    <div className="ap-val">{p.comprador_nombre}</div>
                    {p.comprador_telefono && (
                      <div className="ap-sub">📱 {p.comprador_telefono}
                        <button className="ap-copy" title="Copiar" onClick={() => copiar(p.comprador_telefono)}>⧉</button>
                      </div>
                    )}
                    {p.comprador_email && <div className="ap-sub">📧 {p.comprador_email}</div>}
                  </div>
                  <div>
                    <div className="ap-tag">Tipo</div>
                    <div className="ap-val">{TIPO_LABEL[p.tipo] || p.tipo}</div>
                    <div className="ap-sub">{resumen}</div>
                  </div>
                  <div>
                    <div className="ap-tag">Entrega</div>
                    <div className="ap-val">{fmt(p.fecha_entrega_programada)}</div>
                    <div className="ap-sub" style={{ textTransform: 'capitalize' }}>
                      {p.entrega_dia_semana} · {labelCortoVentana(p.entrega_ventana)}
                    </div>
                    <div className="ap-sub">{p.entrega_direccion}{p.entrega_apto && `, ${p.entrega_apto}`}
                      <button className="ap-copy" title="Copiar" onClick={() => copiar(`${p.entrega_direccion}${p.entrega_apto ? ', ' + p.entrega_apto : ''}`)}>⧉</button>
                    </div>
                  </div>
                  <div>
                    <div className="ap-tag">Total</div>
                    <div className="ap-val ap-monto">${Number(p.monto_total).toLocaleString('es-UY')}</div>
                    <div className="ap-sub">
                      Sub ${Number(p.monto_subtotal).toLocaleString('es-UY')} + envío ${Number(p.costo_envio).toLocaleString('es-UY')}
                    </div>
                    {p.metodo_pago && <div className="ap-sub">💳 {p.metodo_pago}</div>}
                    {lib && (
                      <div className="ap-sub" style={{ marginTop: 4 }}>
                        <span className="ap-lib" data-estado={lib.estado}
                          title={lib.estado === 'retenido'
                            ? `Mercado Pago retiene este pago. Se libera el ${libDateLarga(lib.fecha)}${lib.monto ? ' · neto $' + Number(lib.monto).toLocaleString('es-UY') : ''}`
                            : `Pago liberado por MP${lib.aprox ? ' (estimado, falta confirmación)' : ''} el ${libDateLarga(lib.fecha)}`}>
                          {lib.estado === 'retenido' ? '🔒 Retenido' : '🔓 Liberado'} {libDateCorta(lib.fecha)}{lib.aprox ? '~' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Detalle de items expandible */}
                {isOpen && items.length > 0 && (
                  <div className="ap-items">
                    <table className="ap-items-tabla">
                      <thead>
                        <tr>
                          <th style={{ width: 46 }}>SKU</th>
                          <th>Producto</th>
                          <th style={{ width: 80 }}>Present.</th>
                          <th style={{ width: 40, textAlign: 'right' }}>Cant</th>
                          <th style={{ width: 70, textAlign: 'right' }}>Unit</th>
                          <th style={{ width: 70, textAlign: 'right' }}>Subtot</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, i) => {
                          const cant = Number(it.cantidad || 1)
                          const unit = Number(it.precio_unit || it.precio_unitario || it.precio || 0)
                          const nombre = [it.marca, it.nombre].filter(Boolean).join(' · ') || it.descripcion || '—'
                          return (
                            <tr key={i}>
                              <td className="ap-items-sku">{it.sku || it.codigo || '—'}</td>
                              <td>{nombre}</td>
                              <td className="ap-items-mono">{it.presentacion || '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{cant}</td>
                              <td style={{ textAlign: 'right' }}>${unit.toLocaleString('es-UY')}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: '#1A3461' }}>${(cant * unit).toLocaleString('es-UY')}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Facturación (snapshot al crear pedido) — solo si expandido */}
                {isOpen && (() => {
                  const rut = p.facturacion_rut
                    || (String(p.comprador_documento_tipo || '').toLowerCase() === 'rut' ? p.comprador_documento : null)
                    || p._empresa?.rut
                  const esFactura = !!(rut && String(rut).replace(/\D/g, '').length === 12)
                  const nombre = p.facturacion_razon_social || p._empresa?.razon_social || p.comprador_nombre
                  const email = p.facturacion_email || p._empresa?.email_facturacion
                  return (
                    <div className={`ap-factura ${esFactura ? 'is-empresa' : 'is-cf'}`}>
                      <span className="ap-factura-icon">🧾</span>
                      <span className="ap-factura-tag">Facturación:</span>
                      {esFactura ? (
                        <>
                          <span className="ap-factura-pill emp">🏢 e-Factura</span>
                          <strong>{nombre}</strong>
                          <span className="ap-factura-rut">RUT {fmtRut(rut)}</span>
                          {email && <span className="ap-factura-sub">→ {email}</span>}
                        </>
                      ) : (
                        <>
                          <span className="ap-factura-pill cf">👤 e-Ticket</span>
                          <strong>{nombre}</strong>
                          {p.empresa_id && <span className="ap-factura-sub">⚠ empresa sin RUT cargado</span>}
                        </>
                      )}
                    </div>
                  )
                })()}

                {/* Acciones — solo si expandido */}
                {isOpen && (
                <div className="ap-pedido-actions">
                  {p.estado === 'pendiente_pago' && (
                    <>
                      <button className="ap-btn-ok" onClick={() => setAccion({ tipo: 'marcar_pagado', pedido: p })}>
                        ✓ Marcar pagado
                      </button>
                      <button className="ap-btn-ghost" onClick={async () => {
                        const tel = (p.comprador_telefono || '').replace(/\D/g, '')
                        if (!tel) { alert('Sin teléfono'); return }
                        const t598 = tel.startsWith('598') ? tel : '598' + tel.replace(/^0/, '')
                        const cuenta = await getCuentaCobro()
                        const nombre = p.comprador_nombre.split(' ')[0]
                        const monto = Number(p.monto_total).toLocaleString('es-UY')
                        const cuentaTxt = cuenta?.banco
                          ? `\n\n📋 *Datos para transferir:*\n• Banco: ${cuenta.banco}\n• Cuenta: ${cuenta.numero || '—'}\n• Titular: ${cuenta.titular || '—'}${cuenta.alias ? `\n• Alias: ${cuenta.alias}` : ''}`
                          : ''
                        const msg = `Hola ${nombre}! 👋\n\nGracias por tu pedido en *Limpack* (${p.numero_orden}).\n\n💰 Total a pagar: *$${monto}*${cuentaTxt}\n\n📲 Mandame comprobante por acá y lo confirmo.`
                        window.open(`https://wa.me/${t598}?text=${encodeURIComponent(msg)}`, '_blank')
                      }}>📲 WhatsApp</button>
                    </>
                  )}
                  {p.estado === 'pagado' && (
                    <button className="ap-btn-ok" onClick={() => setAccion({ tipo: 'en_armado', pedido: p })}>📦 En armado</button>
                  )}
                  {p.estado === 'en_armado' && (
                    <button className="ap-btn-ok" onClick={() => setAccion({ tipo: 'en_ruta', pedido: p })}>🚐 En ruta</button>
                  )}
                  {['pagado', 'en_armado', 'en_ruta'].includes(p.estado) && (
                    <button className="ap-btn-ok" onClick={() => setAccion({ tipo: 'entregado', pedido: p })}>✅ Entregar</button>
                  )}

                  {/* Libertad total: estado libre + editar (siempre, incluso terminales) */}
                  <button className="ap-btn-estado" onClick={() => setEstadoModal(p)} title="Cambiar a cualquier estado / reabrir">
                    ⇅ Estado
                  </button>
                  <button className="ap-btn-ghost" onClick={() => setEditarModal(p)} title="Editar cliente, facturación, ítems, montos, notas">
                    ✏️ Editar
                  </button>
                  <button className="ap-btn-ghost" onClick={() => setRegaloModal(p)} title="Dar un regalo: emite comprobante de entrega gratuita y descuenta stock (no se cobra, no manda mail)">
                    🎁 Regalo
                  </button>
                  {!esTerminal && (
                    <button className="ap-btn-ghost" onClick={() => setAccion({ tipo: 'editar_envio', pedido: p })}
                            title="Cambiar dirección, día, ventana horaria o notas">
                      📦 Envío
                    </button>
                  )}
                  {!esTerminal && (
                    <button className="ap-btn-link-danger" onClick={() => setAccion({ tipo: 'cancelar', pedido: p })}>
                      Cancelar
                    </button>
                  )}
                </div>
                )}

                {/* Notas colapsables — solo primera línea si card cerrada; completo si abierta */}
                {p.notas && isOpen && (
                  <details className="ap-notas-details" open>
                    <summary>📝 Notas</summary>
                    <div className="ap-notas-content">{p.notas}</div>
                  </details>
                )}
              </div>
            )
          })}
        </div>

        {accion && <ModalAccion accion={accion} onClose={() => setAccion(null)} onDone={refrescar} />}
        {estadoModal && <ModalEstado pedido={estadoModal} esSuper={esSuper} onClose={() => setEstadoModal(null)} onDone={refrescar} />}
        {editarModal && <ModalEditar pedido={editarModal} onClose={() => setEditarModal(null)} onDone={refrescar} />}
        {regaloModal && <ModalRegalo pedido={regaloModal} onClose={() => setRegaloModal(null)} onDone={refrescar} />}
        {crearOpen && <ModalCrear onClose={() => setCrearOpen(false)} onDone={refrescar} />}

        <style>{styles}</style>
      </div>
    </AdminLayout>
  )
}

/* ════════════════════ ModalAccion (flujos existentes) ════════════════════ */
const ACCION_CONFIG = {
  marcar_pagado: { titulo: '✓ Confirmar pago', boton: 'Confirmar pago', busy: 'Confirmando…', clase: 'ap-btn-ok' },
  cancelar:      { titulo: '❌ Cancelar pedido', boton: 'Sí, cancelar', busy: 'Cancelando…', clase: 'ap-btn-danger' },
  en_armado:     { titulo: '📦 Marcar como en armado', boton: 'Sí, en armado', busy: 'Actualizando…', clase: 'ap-btn-ok' },
  en_ruta:       { titulo: '🚐 Marcar como en ruta', boton: 'Sí, en ruta', busy: 'Actualizando…', clase: 'ap-btn-ok' },
  entregado:     { titulo: '✅ Marcar como entregado', boton: 'Sí, entregado', busy: 'Entregando + descontando stock…', clase: 'ap-btn-ok' },
  editar_envio:  { titulo: '📦 Editar envío', boton: 'Guardar cambios', busy: 'Guardando…', clase: 'ap-btn-ok' },
}

function ModalAccion({ accion, onClose, onDone }) {
  const { tipo, pedido } = accion
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [referencia, setReferencia] = useState('')
  const [motivo, setMotivo] = useState('')
  const [descontarStock, setDescontarStock] = useState(true)
  const [resultado, setResultado] = useState(null)

  const [envioForm, setEnvioForm] = useState(() => ({
    fecha: pedido.fecha_entrega_programada || '',
    ventana: pedido.entrega_ventana || 'manana',
    direccion: pedido.entrega_direccion || '',
    apto: pedido.entrega_apto || '',
    notas: pedido.notas || '',
    ignorarBarrioDias: false,
  }))
  const [esSuper, setEsSuper] = useState(false)
  useEffect(() => {
    if (tipo !== 'editar_envio') return
    supabase.rpc('is_superadmin').then(({ data }) => setEsSuper(!!data))
  }, [tipo])

  const cfg = ACCION_CONFIG[tipo] || ACCION_CONFIG.cancelar

  async function ok() {
    setBusy(true); setErr(null); setResultado(null)
    try {
      if (tipo === 'marcar_pagado') {
        await adminMarcarPedidoPagado(pedido.id, referencia || null)
      } else if (tipo === 'cancelar') {
        if (!confirm(`¿Seguro que querés cancelar ${pedido.numero_orden}?`)) { setBusy(false); return }
        await adminCancelarPedido(pedido.id, motivo || null)
      } else if (tipo === 'en_armado') {
        await adminMarcarPedidoEnArmado(pedido.id)
      } else if (tipo === 'en_ruta') {
        await adminMarcarPedidoEnRuta(pedido.id)
      } else if (tipo === 'entregado') {
        const res = await adminMarcarPedidoEntregado(pedido.id, descontarStock)
        if (res?.stock_detalle && Array.isArray(res.stock_detalle) && res.stock_detalle.length > 0) {
          setResultado(res); setBusy(false); return
        }
      } else if (tipo === 'editar_envio') {
        const patch = {}
        if (envioForm.fecha && envioForm.fecha !== pedido.fecha_entrega_programada) patch.fecha_entrega_programada = envioForm.fecha
        if (envioForm.ventana && envioForm.ventana !== pedido.entrega_ventana) patch.entrega_ventana = envioForm.ventana
        if (envioForm.direccion.trim() && envioForm.direccion !== pedido.entrega_direccion) patch.entrega_direccion = envioForm.direccion.trim()
        if (envioForm.apto !== (pedido.entrega_apto || '')) patch.entrega_apto = envioForm.apto || null
        if (envioForm.notas !== (pedido.notas || '')) patch.notas = envioForm.notas || null
        if (Object.keys(patch).length === 0) { setErr('No modificaste ningún dato'); setBusy(false); return }
        const res = await adminEditarEnvioPedido(pedido.id, patch, {
          ignorarBarrioDias: envioForm.ignorarBarrioDias && esSuper, motivo: motivo || null,
        })
        setResultado(res); setBusy(false); return
      }
      onDone()
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="ap-modal-bg" onClick={onClose}>
      <div className="ap-modal" onClick={e => e.stopPropagation()}>
        <h2>{cfg.titulo} · {pedido.numero_orden}</h2>
        <div className="ap-modal-info">
          <div><strong>Cliente:</strong> {pedido.comprador_nombre}</div>
          <div><strong>Total:</strong> ${Number(pedido.monto_total).toLocaleString('es-UY')}</div>
        </div>

        {tipo === 'marcar_pagado' && (
          <label className="ap-field">
            <b>Referencia (nro transferencia, ticket, etc.)</b>
            <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="ej: TR-2026-0123" />
          </label>
        )}

        {tipo === 'cancelar' && (
          <label className="ap-field">
            <b>Motivo (opcional)</b>
            <textarea rows="2" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Cliente pidió cancelar, sin pago, etc." />
          </label>
        )}

        {tipo === 'entregado' && !resultado && (
          <>
            <label className="ap-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={descontarStock} onChange={e => setDescontarStock(e.target.checked)} style={{ margin: 0 }} />
              <span><b>Descontar stock</b> — resta las unidades entregadas del inventario</span>
            </label>
            <div className="ap-info-box">
              Vas a: (1) pasar el pedido a <b>entregado</b>, {descontarStock ? '(2) descontar stock de los productos, ' : ''}(3) registrar la acción.
            </div>
          </>
        )}

        {tipo === 'editar_envio' && !resultado && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label className="ap-field">
                <b>Fecha de entrega</b>
                <input type="date" value={envioForm.fecha}
                       onChange={e => setEnvioForm(f => ({ ...f, fecha: e.target.value }))}
                       min={new Date().toISOString().slice(0, 10)} />
              </label>
              <label className="ap-field">
                <b>Ventana horaria</b>
                <select value={envioForm.ventana} onChange={e => setEnvioForm(f => ({ ...f, ventana: e.target.value }))}>
                  {opcionesVentana().map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            </div>
            <label className="ap-field">
              <b>Dirección</b>
              <input type="text" value={envioForm.direccion}
                     onChange={e => setEnvioForm(f => ({ ...f, direccion: e.target.value }))} placeholder="ej: Manuel Pagola 3336" />
            </label>
            <label className="ap-field">
              <b>Apto / piso (opcional)</b>
              <input type="text" value={envioForm.apto}
                     onChange={e => setEnvioForm(f => ({ ...f, apto: e.target.value }))} placeholder="ej: 501" />
            </label>
            <label className="ap-field">
              <b>Notas de entrega (opcional)</b>
              <textarea rows="2" value={envioForm.notas}
                        onChange={e => setEnvioForm(f => ({ ...f, notas: e.target.value }))} placeholder="Tocar timbre, dejar en portería, etc." />
            </label>
            {esSuper && (
              <label className="ap-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, background: '#fef3c7', padding: 10, borderRadius: 8 }}>
                <input type="checkbox" checked={envioForm.ignorarBarrioDias}
                       onChange={e => setEnvioForm(f => ({ ...f, ignorarBarrioDias: e.target.checked }))} style={{ margin: 0 }} />
                <span style={{ color: '#92400e' }}><b>⚡ Superadmin:</b> ignorar días de reparto del barrio</span>
              </label>
            )}
            <label className="ap-field">
              <b>Motivo del cambio (opcional, audit)</b>
              <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="ej: Cliente pidió adelantar" />
            </label>
          </>
        )}

        {tipo === 'editar_envio' && resultado?.campos_cambiados && (
          <div className="ap-info-box" style={{ background: '#f0fdf4', borderColor: '#16a34a' }}>
            <b style={{ color: '#166534' }}>✅ Envío actualizado.</b> Campos: {resultado.campos_cambiados.join(', ') || 'ninguno'}.
          </div>
        )}

        {resultado?.stock_detalle && (
          <div className="ap-info-box" style={{ background: '#f0fdf4', borderColor: '#16a34a', maxHeight: 220, overflowY: 'auto' }}>
            <b style={{ color: '#166534' }}>✅ Pedido entregado. Stock descontado:</b>
            <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
              <thead>
                <tr><th style={{ textAlign: 'left' }}>SKU</th><th style={{ textAlign: 'right' }}>Cant</th><th style={{ textAlign: 'right' }}>Antes</th><th style={{ textAlign: 'right' }}>Después</th></tr>
              </thead>
              <tbody>
                {resultado.stock_detalle.map((r, i) => (
                  <tr key={i} style={{ color: r.matched ? '#1a3461' : '#991b1b' }}>
                    <td>{r.item_sku || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.item_cantidad}</td>
                    <td style={{ textAlign: 'right' }}>{r.matched ? r.stock_antes : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.matched ? r.stock_despues : '⚠ sin match'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {err && <div className="ap-err">{err}</div>}

        <div className="ap-modal-foot">
          {resultado ? (
            <button className="ap-btn-ok" onClick={onDone}>Listo, cerrar</button>
          ) : (
            <>
              <button className="ap-btn-ghost" onClick={onClose}>Cerrar</button>
              <button className={cfg.clase} onClick={ok} disabled={busy}>{busy ? cfg.busy : cfg.boton}</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ════════════════════ ModalEstado — estado libre / reabrir ════════════════════ */
function ModalEstado({ pedido, esSuper, onClose, onDone }) {
  const entregado = pedido.estado === 'entregado'
  const [destino, setDestino] = useState(entregado ? 'pagado' : '')
  const [motivo, setMotivo] = useState('')
  const [reponer, setReponer] = useState(true)
  const [descontar, setDescontar] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [res, setRes] = useState(null)

  const opciones = entregado
    ? ESTADOS_REABRIR
    : ESTADOS_LIBRES.filter(e => e !== pedido.estado).concat('entregado')

  async function ok() {
    if (!destino) { setErr('Elegí un estado destino'); return }
    setBusy(true); setErr(null); setRes(null)
    try {
      if (entregado) {
        if (!esSuper) throw new Error('Solo superadmin puede reabrir un pedido entregado')
        const r = await adminReabrirPedidoEntregado(pedido.id, destino, reponer, motivo || null)
        setRes({ tipo: 'reabrir', ...r }); setBusy(false); return
      }
      if (destino === 'entregado') {
        const r = await adminMarcarPedidoEntregado(pedido.id, descontar)
        setRes({ tipo: 'entregado', ...r }); setBusy(false); return
      }
      const r = await adminCambiarEstadoPedido(pedido.id, destino, motivo || null)
      setRes({ tipo: 'cambio', ...r }); setBusy(false)
      if (!r?.nota) { onDone(); return }
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="ap-modal-bg" onClick={onClose}>
      <div className="ap-modal" onClick={e => e.stopPropagation()}>
        <h2>{entregado ? '↩ Reabrir pedido entregado' : '⇅ Cambiar estado'} · {pedido.numero_orden}</h2>
        <div className="ap-modal-info">
          <div><strong>Estado actual:</strong> {ESTADO_PILL[pedido.estado]?.label || pedido.estado}</div>
          <div><strong>Cliente:</strong> {pedido.comprador_nombre}</div>
        </div>

        {!res && (
          <>
            {entregado && !esSuper && (
              <div className="ap-err">Reabrir un pedido entregado es solo para superadmin.</div>
            )}
            <label className="ap-field">
              <b>{entregado ? 'Reabrir hacia' : 'Nuevo estado'}</b>
              <select value={destino} onChange={e => setDestino(e.target.value)}>
                {!entregado && <option value="">— Elegí —</option>}
                {opciones.map(e => (
                  <option key={e} value={e}>{ESTADO_PILL[e]?.label || e}{e === 'entregado' ? ' (descuenta stock)' : ''}</option>
                ))}
              </select>
            </label>

            {entregado && (
              <>
                <label className="ap-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" checked={reponer} onChange={e => setReponer(e.target.checked)} style={{ margin: 0 }} />
                  <span><b>Reponer stock</b> — devuelve al inventario lo que se había descontado</span>
                </label>
                <div className="ap-info-box" style={{ background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
                  ⚠ Reabrir no toca la DGI ni los asientos. Si el pedido tenía CFE emitida o asiento de costo, te lo aviso al terminar para que lo veas con Roberto (nota de crédito si corresponde).
                </div>
              </>
            )}
            {!entregado && destino === 'entregado' && (
              <label className="ap-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" checked={descontar} onChange={e => setDescontar(e.target.checked)} style={{ margin: 0 }} />
                <span><b>Descontar stock</b></span>
              </label>
            )}
            {!entregado && (
              <label className="ap-field">
                <b>Motivo (opcional, audit)</b>
                <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="ej: corrección, cliente reprogramó" />
              </label>
            )}
            {entregado && (
              <label className="ap-field">
                <b>Motivo (opcional, audit)</b>
                <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="ej: entrega fallida, error de carga" />
              </label>
            )}
          </>
        )}

        {res && (
          <div className="ap-info-box" style={{ background: '#f0fdf4', borderColor: '#16a34a' }}>
            <b style={{ color: '#166534' }}>✅ Estado actualizado a {ESTADO_PILL[res.estado_final || res.estado || res.a]?.label || res.estado_final || res.estado || res.a}.</b>
            {res.nota && <div style={{ marginTop: 6, color: '#92400e' }}>ℹ {res.nota}</div>}
            {res.tipo === 'reabrir' && Array.isArray(res.stock_repuesto) && res.stock_repuesto.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Stock repuesto: {res.stock_repuesto.map((r, i) => (
                  <span key={i}>{r.sku || '?'} (+{r.cantidad}{r.matched === false ? ' ⚠ sin match' : ''}){i < res.stock_repuesto.length - 1 ? ', ' : ''}</span>
                ))}
              </div>
            )}
            {res.tipo === 'reabrir' && Array.isArray(res.warnings) && res.warnings.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b' }}>
                {res.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
          </div>
        )}

        {err && <div className="ap-err">{err}</div>}

        <div className="ap-modal-foot">
          {res ? (
            <button className="ap-btn-ok" onClick={onDone}>Listo, cerrar</button>
          ) : (
            <>
              <button className="ap-btn-ghost" onClick={onClose}>Cerrar</button>
              <button className="ap-btn-ok" onClick={ok} disabled={busy || (entregado && !esSuper)}>
                {busy ? 'Aplicando…' : (entregado ? 'Reabrir' : 'Cambiar estado')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ════════════════════ ModalRegalo — regalo (entrega gratuita) ════════════════════ */
function ModalRegalo({ pedido, onClose, onDone }) {
  const [catalogo, setCatalogo] = useState([])
  const [q, setQ] = useState('')
  const [openList, setOpenList] = useState(false)
  const [regalos, setRegalos] = useState([])
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [res, setRes] = useState(null)

  useEffect(() => {
    supabase.from('productos').select('id, sku, nombre, marca, presentacion, stock_actual, stock_reservado')
      .eq('activo', true).order('nombre')
      .then(({ data }) => setCatalogo(data || []))
  }, [])

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase()
    const base = t ? catalogo.filter(p => [p.nombre, p.marca, p.sku, p.presentacion].filter(Boolean).join(' ').toLowerCase().includes(t)) : catalogo
    return base.slice(0, 30)
  }, [q, catalogo])

  const disp = (p) => Number(p.stock_actual || 0) - Number(p.stock_reservado || 0)

  function agregar(prod) {
    setRegalos(prev => {
      const idx = prev.findIndex(r => r.producto_id === prod.id)
      if (idx >= 0) { const n = [...prev]; n[idx] = { ...n[idx], cantidad: Number(n[idx].cantidad || 1) + 1 }; return n }
      return [...prev, { producto_id: prod.id, sku: prod.sku, nombre: prod.nombre, marca: prod.marca, cantidad: 1, stock: disp(prod) }]
    })
    setQ(''); setOpenList(false)
  }
  const setCant = (i, v) => setRegalos(prev => prev.map((r, j) => j === i ? { ...r, cantidad: Math.max(1, Number(v) || 1) } : r))
  const quitar = (i) => setRegalos(prev => prev.filter((_, j) => j !== i))

  async function emitir() {
    if (regalos.length === 0) { setErr('Agregá al menos un producto'); return }
    setBusy(true); setErr(null); setRes(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/emitir-regalo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productos: regalos.map(r => ({ producto_id: r.producto_id, cantidad: r.cantidad })),
          pedido_id: pedido.id,
          motivo: motivo || `Regalo para ${pedido.comprador_nombre || ''} (${pedido.numero_orden})`,
        }),
      })
      const data = await r.json()
      if (!r.ok || !data.ok) throw new Error(data.biller_error || data.error || 'No se pudo emitir el regalo')
      setRes(data); setBusy(false)
    } catch (e) { setErr(String(e.message || e)); setBusy(false) }
  }

  return (
    <div className="ap-modal-bg" onClick={onClose}>
      <div className="ap-modal" onClick={e => e.stopPropagation()}>
        <h2>🎁 Dar un regalo · {pedido.numero_orden}</h2>
        <div className="ap-modal-info">
          <div><strong>Cliente:</strong> {pedido.comprador_nombre || '—'}</div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
            Emite un comprobante de <b>entrega gratuita</b> ($0), descuenta del stock y <b>no le manda mail</b> al cliente. No forma parte del ticket de la compra.
          </div>
        </div>

        {!res ? (
          <>
            <div className="ap-buscador">
              <input type="text" value={q} placeholder="Buscar producto por nombre, marca o SKU…"
                     onChange={e => { setQ(e.target.value); setOpenList(true) }} onFocus={() => setOpenList(true)} />
              {openList && filtrados.length > 0 && (
                <div className="ap-buscador-list">
                  {filtrados.map(p => (
                    <button type="button" key={p.id} className="ap-buscador-item" onClick={() => agregar(p)}>
                      <span><code>{p.sku}</code> {[p.marca, p.nombre].filter(Boolean).join(' · ')} <em>{p.presentacion}</em></span>
                      <span className="ap-buscador-precio">disp {disp(p)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {regalos.length === 0 ? (
              <div className="ap-items-vacio">Buscá y agregá el/los producto(s) de regalo.</div>
            ) : (
              <table className="ap-items-tabla ap-items-edit">
                <thead><tr><th>Producto</th><th style={{ width: 70, textAlign: 'right' }}>Cant</th><th style={{ width: 30 }}></th></tr></thead>
                <tbody>
                  {regalos.map((r, i) => (
                    <tr key={i}>
                      <td><div style={{ fontWeight: 600 }}>{[r.marca, r.nombre].filter(Boolean).join(' · ')}</div><code className="ap-items-sku">{r.sku} · disp {r.stock}</code></td>
                      <td style={{ textAlign: 'right' }}><input className="ap-mini-input" type="number" min="1" step="1" value={r.cantidad} onChange={e => setCant(i, e.target.value)} /></td>
                      <td><button type="button" className="ap-btn-link-danger" onClick={() => quitar(i)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <label className="ap-field">
              <b>Motivo (opcional)</b>
              <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: fidelización, disculpa por demora…" />
            </label>

            {err && <div className="ap-err">{err}</div>}

            <div className="ap-modal-foot">
              <button className="ap-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
              <button className="ap-btn-ok" onClick={emitir} disabled={busy || regalos.length === 0}>
                {busy ? 'Emitiendo…' : '🎁 Emitir regalo + descontar stock'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', margin: '4px 0' }}>
              <b style={{ color: '#166534' }}>✅ Regalo emitido.</b> Comprobante de entrega gratuita <b>{res.serie}-{res.numero}</b> ($0). Sin mail al cliente.
            </div>
            {Array.isArray(res.stock_result) && res.stock_result.length > 0 && (
              <div className="ap-modal-info">
                <div><strong>Stock descontado:</strong></div>
                {res.stock_result.map((s, i) => <div key={i} style={{ fontSize: 13 }}>{s.sku}: {s.error ? `⚠ ${s.error}` : `stock ${s.stock_actual}`}</div>)}
              </div>
            )}
            <div className="ap-modal-foot">
              <button className="ap-btn-ok" onClick={onDone}>Listo, cerrar</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ════════════════════ ItemsEditor (compartido) ════════════════════ */
function ItemsEditor({ items, setItems }) {
  const [catalogo, setCatalogo] = useState([])
  const [q, setQ] = useState('')
  const [openList, setOpenList] = useState(false)

  useEffect(() => {
    supabase.from('productos')
      .select('id, sku, nombre, marca, presentacion, precio_publico, stock_actual')
      .eq('activo', true).order('nombre')
      .then(({ data }) => setCatalogo(data || []))
  }, [])

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return catalogo.slice(0, 30)
    return catalogo.filter(p =>
      [p.nombre, p.marca, p.sku, p.presentacion].filter(Boolean).join(' ').toLowerCase().includes(t)
    ).slice(0, 30)
  }, [q, catalogo])

  function agregar(prod) {
    setItems(prev => {
      const idx = prev.findIndex(it => it.producto_id === prod.id)
      if (idx >= 0) {
        const nuevo = [...prev]; nuevo[idx] = { ...nuevo[idx], cantidad: Number(nuevo[idx].cantidad || 1) + 1 }
        return nuevo
      }
      return [...prev, {
        producto_id: prod.id, sku: prod.sku, nombre: prod.nombre, marca: prod.marca,
        presentacion: prod.presentacion, cantidad: 1, precio_unit: ceil(prod.precio_publico),
      }]
    })
    setQ(''); setOpenList(false)
  }
  const setCampo = (i, campo, val) => setItems(prev => prev.map((it, j) => j === i ? { ...it, [campo]: val } : it))
  const quitar = (i) => setItems(prev => prev.filter((_, j) => j !== i))
  const subtotal = items.reduce((s, it) => s + Number(it.cantidad || 1) * Number(it.precio_unit || 0), 0)

  return (
    <div className="ap-items-editor">
      <div className="ap-buscador">
        <input type="text" value={q} placeholder="Buscar producto por nombre, marca o SKU…"
               onChange={e => { setQ(e.target.value); setOpenList(true) }} onFocus={() => setOpenList(true)} />
        {openList && filtrados.length > 0 && (
          <div className="ap-buscador-list">
            {filtrados.map(p => (
              <button type="button" key={p.id} className="ap-buscador-item" onClick={() => agregar(p)}>
                <span><code>{p.sku}</code> {[p.marca, p.nombre].filter(Boolean).join(' · ')} <em>{p.presentacion}</em></span>
                <span className="ap-buscador-precio">${ceil(p.precio_publico).toLocaleString('es-UY')} · stock {Number(p.stock_actual || 0)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="ap-items-vacio">Sin ítems. Buscá y agregá productos arriba.</div>
      ) : (
        <table className="ap-items-tabla ap-items-edit">
          <thead>
            <tr>
              <th>Producto</th>
              <th style={{ width: 64, textAlign: 'right' }}>Cant</th>
              <th style={{ width: 90, textAlign: 'right' }}>Unit</th>
              <th style={{ width: 80, textAlign: 'right' }}>Subtot</th>
              <th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td>
                  <div style={{ fontWeight: 600 }}>{[it.marca, it.nombre].filter(Boolean).join(' · ') || it.descripcion || '—'}</div>
                  <code className="ap-items-sku">{it.sku || '—'} · {it.presentacion || ''}</code>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <input className="ap-mini-input" type="number" min="0" step="1" value={it.cantidad}
                         onChange={e => setCampo(i, 'cantidad', Number(e.target.value))} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <input className="ap-mini-input" type="number" min="0" step="1" value={it.precio_unit}
                         onChange={e => setCampo(i, 'precio_unit', Number(e.target.value))}
                         onBlur={e => setCampo(i, 'precio_unit', ceil(e.target.value))} />
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#1A3461' }}>
                  ${(Number(it.cantidad || 1) * Number(it.precio_unit || 0)).toLocaleString('es-UY')}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button type="button" className="ap-quitar" onClick={() => quitar(i)} title="Quitar">×</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan="3" style={{ textAlign: 'right', fontWeight: 700 }}>Subtotal</td>
              <td style={{ textAlign: 'right', fontWeight: 800, color: '#1A3461' }}>${subtotal.toLocaleString('es-UY')}</td><td></td></tr>
          </tfoot>
        </table>
      )}
    </div>
  )
}

/* ════════════════════ ModalEditar — pedido completo ════════════════════ */
function ModalEditar({ pedido, onClose, onDone }) {
  const bloqueadoItems = pedido.estado === 'entregado' || !!pedido.stock_descontado_at
  const [f, setF] = useState(() => ({
    comprador_nombre: pedido.comprador_nombre || '',
    comprador_telefono: pedido.comprador_telefono || '',
    comprador_email: pedido.comprador_email || '',
    comprador_documento: pedido.comprador_documento || '',
    facturacion_razon_social: pedido.facturacion_razon_social || '',
    facturacion_rut: pedido.facturacion_rut || '',
    facturacion_email: pedido.facturacion_email || '',
    facturacion_direccion: pedido.facturacion_direccion || '',
    notas: pedido.notas || '',
    costo_envio: Number(pedido.costo_envio || 0),
  }))
  const [items, setItems] = useState(() => (Array.isArray(pedido.items) ? pedido.items : []).map(it => ({
    producto_id: it.producto_id || null, sku: it.sku || it.codigo || '', nombre: it.nombre || it.descripcion || '',
    marca: it.marca || '', presentacion: it.presentacion || '',
    cantidad: Number(it.cantidad || 1), precio_unit: Number(it.precio_unit || it.precio_unitario || it.precio || 0),
  })))
  const [editarItems, setEditarItems] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))
  const subtotalItems = items.reduce((s, it) => s + Number(it.cantidad || 1) * Number(it.precio_unit || 0), 0)
  const total = (editarItems ? subtotalItems : Number(pedido.monto_subtotal || 0)) + Number(f.costo_envio || 0)

  async function guardar() {
    setBusy(true); setErr(null)
    try {
      const patch = {}
      const campos = ['comprador_nombre', 'comprador_telefono', 'comprador_email', 'comprador_documento',
        'facturacion_razon_social', 'facturacion_rut', 'facturacion_email', 'facturacion_direccion', 'notas']
      for (const c of campos) {
        const orig = pedido[c] || ''
        if ((f[c] || '') !== orig) patch[c] = f[c] || null
      }
      if (!bloqueadoItems) {
        if (editarItems) {
          patch.items = items.map(it => ({
            producto_id: it.producto_id, sku: it.sku, nombre: it.nombre, marca: it.marca,
            presentacion: it.presentacion, cantidad: Number(it.cantidad || 1), precio_unit: ceil(it.precio_unit),
          }))
        }
        if (Number(f.costo_envio || 0) !== Number(pedido.costo_envio || 0)) patch.costo_envio = Number(f.costo_envio || 0)
      }
      if (Object.keys(patch).length === 0) { setErr('No modificaste nada'); setBusy(false); return }
      await adminEditarPedido(pedido.id, patch, null)
      onDone()
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="ap-modal-bg" onClick={onClose}>
      <div className="ap-modal ap-modal-wide" onClick={e => e.stopPropagation()}>
        <h2>✏️ Editar pedido · {pedido.numero_orden}</h2>

        <div className="ap-sec">Cliente</div>
        <div className="ap-grid2">
          <label className="ap-field"><b>Nombre</b>
            <input value={f.comprador_nombre} onChange={e => set('comprador_nombre', e.target.value)} /></label>
          <label className="ap-field"><b>Teléfono</b>
            <input value={f.comprador_telefono} onChange={e => set('comprador_telefono', e.target.value)} /></label>
          <label className="ap-field"><b>Email</b>
            <input value={f.comprador_email} onChange={e => set('comprador_email', e.target.value)} /></label>
          <label className="ap-field"><b>Documento</b>
            <input value={f.comprador_documento} onChange={e => set('comprador_documento', e.target.value)} /></label>
        </div>

        <div className="ap-sec">Facturación</div>
        <div className="ap-info-box" style={{ fontSize: 12 }}>
          Si cargás un RUT (12 dígitos) se emite e-Factura; sin RUT, e-Ticket. Si el pedido ya tiene CFE emitida no vas a poder cambiar estos datos (hay que hacer nota de crédito).
        </div>
        <div className="ap-grid2">
          <label className="ap-field"><b>Razón social</b>
            <input value={f.facturacion_razon_social} onChange={e => set('facturacion_razon_social', e.target.value)} /></label>
          <label className="ap-field"><b>RUT</b>
            <input value={f.facturacion_rut} onChange={e => set('facturacion_rut', e.target.value)} placeholder="12 dígitos" /></label>
          <label className="ap-field"><b>Email facturación</b>
            <input value={f.facturacion_email} onChange={e => set('facturacion_email', e.target.value)} /></label>
          <label className="ap-field"><b>Dirección fiscal</b>
            <input value={f.facturacion_direccion} onChange={e => set('facturacion_direccion', e.target.value)} /></label>
        </div>

        <div className="ap-sec">Ítems y montos</div>
        {bloqueadoItems ? (
          <div className="ap-info-box" style={{ background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
            🔒 El pedido ya está entregado / con stock descontado. Para editar ítems o montos, reabrilo primero (⇅ Estado).
          </div>
        ) : (
          <>
            {!editarItems ? (
              <button type="button" className="ap-btn-ghost" onClick={() => setEditarItems(true)}>Editar ítems</button>
            ) : (
              <ItemsEditor items={items} setItems={setItems} />
            )}
            <div className="ap-grid2" style={{ marginTop: 12 }}>
              <label className="ap-field"><b>Costo de envío</b>
                <input type="number" min="0" step="1" value={f.costo_envio}
                       onChange={e => set('costo_envio', Number(e.target.value))}
                       onBlur={e => set('costo_envio', ceil(e.target.value))} /></label>
              <div className="ap-field">
                <b>Total</b>
                <div className="ap-total-preview">${total.toLocaleString('es-UY')}</div>
              </div>
            </div>
          </>
        )}

        <div className="ap-sec">Notas</div>
        <label className="ap-field">
          <textarea rows="2" value={f.notas} onChange={e => set('notas', e.target.value)} placeholder="Notas internas / de entrega" />
        </label>

        {err && <div className="ap-err">{err}</div>}
        <div className="ap-modal-foot">
          <button className="ap-btn-ghost" onClick={onClose}>Cerrar</button>
          <button className="ap-btn-ok" onClick={guardar} disabled={busy}>{busy ? 'Guardando…' : 'Guardar cambios'}</button>
        </div>
      </div>
    </div>
  )
}

/* ════════════════════ ModalCrear — pedido a mano ════════════════════ */
function ModalCrear({ onClose, onDone }) {
  const [barrios, setBarrios] = useState([])
  const [items, setItems] = useState([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [res, setRes] = useState(null)
  const [f, setF] = useState({
    comprador_nombre: '', comprador_telefono: '', comprador_email: '', comprador_documento: '',
    tipo: 'productos_sueltos', entrega_barrio_id: '', entrega_direccion: '', entrega_apto: '',
    fecha_entrega_programada: '', entrega_ventana: 'manana', costo_envio: 0,
    facturacion_razon_social: '', facturacion_rut: '', facturacion_email: '',
    notas: '', metodo_pago: '', marcar_pagado: false, pago_referencia: '',
  })
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))

  useEffect(() => { adminGetBarrios().then(b => setBarrios(b || [])) }, [])

  const subtotal = items.reduce((s, it) => s + Number(it.cantidad || 1) * Number(it.precio_unit || 0), 0)
  const total = subtotal + Number(f.costo_envio || 0)

  async function crear() {
    setBusy(true); setErr(null)
    try {
      if (!f.comprador_nombre.trim()) throw new Error('Falta el nombre')
      if (!f.fecha_entrega_programada) throw new Error('Falta la fecha de entrega')
      if (!f.entrega_barrio_id) throw new Error('Elegí un barrio')
      if (!f.entrega_direccion.trim()) throw new Error('Falta la dirección')
      if (items.length === 0) throw new Error('Agregá al menos un ítem')
      const data = {
        ...f,
        costo_envio: ceil(f.costo_envio),
        items: items.map(it => ({
          producto_id: it.producto_id, sku: it.sku, nombre: it.nombre, marca: it.marca,
          presentacion: it.presentacion, cantidad: Number(it.cantidad || 1), precio_unit: ceil(it.precio_unit),
        })),
      }
      const r = await adminCrearPedidoManual(data)
      setRes(r); setBusy(false)
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="ap-modal-bg" onClick={onClose}>
      <div className="ap-modal ap-modal-wide" onClick={e => e.stopPropagation()}>
        <h2>+ Nuevo pedido one-shot</h2>

        {res ? (
          <>
            <div className="ap-info-box" style={{ background: '#f0fdf4', borderColor: '#16a34a' }}>
              <b style={{ color: '#166534' }}>✅ Pedido {res.numero_orden} creado</b> — estado {ESTADO_PILL[res.estado]?.label || res.estado}.
            </div>
            <div className="ap-modal-foot"><button className="ap-btn-ok" onClick={onDone}>Listo</button></div>
          </>
        ) : (
          <>
            <div className="ap-sec">Cliente</div>
            <div className="ap-grid2">
              <label className="ap-field"><b>Nombre *</b>
                <input value={f.comprador_nombre} onChange={e => set('comprador_nombre', e.target.value)} /></label>
              <label className="ap-field"><b>Teléfono</b>
                <input value={f.comprador_telefono} onChange={e => set('comprador_telefono', e.target.value)} /></label>
              <label className="ap-field"><b>Email</b>
                <input value={f.comprador_email} onChange={e => set('comprador_email', e.target.value)} /></label>
              <label className="ap-field"><b>Tipo</b>
                <select value={f.tipo} onChange={e => set('tipo', e.target.value)}>
                  <option value="productos_sueltos">🛍️ Tienda (productos sueltos)</option>
                  <option value="compra_unica">📦 Kit único</option>
                </select></label>
            </div>

            <div className="ap-sec">Entrega</div>
            <div className="ap-grid2">
              <label className="ap-field"><b>Barrio *</b>
                <select value={f.entrega_barrio_id} onChange={e => set('entrega_barrio_id', e.target.value)}>
                  <option value="">— Elegí —</option>
                  {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select></label>
              <label className="ap-field"><b>Fecha *</b>
                <input type="date" value={f.fecha_entrega_programada}
                       onChange={e => set('fecha_entrega_programada', e.target.value)}
                       min={new Date().toISOString().slice(0, 10)} /></label>
              <label className="ap-field"><b>Dirección *</b>
                <input value={f.entrega_direccion} onChange={e => set('entrega_direccion', e.target.value)} /></label>
              <label className="ap-field"><b>Apto / piso</b>
                <input value={f.entrega_apto} onChange={e => set('entrega_apto', e.target.value)} /></label>
              <label className="ap-field"><b>Ventana</b>
                <select value={f.entrega_ventana} onChange={e => set('entrega_ventana', e.target.value)}>
                  {opcionesVentana().map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select></label>
              <label className="ap-field"><b>Costo de envío</b>
                <input type="number" min="0" step="1" value={f.costo_envio}
                       onChange={e => set('costo_envio', Number(e.target.value))}
                       onBlur={e => set('costo_envio', ceil(e.target.value))} /></label>
            </div>

            <div className="ap-sec">Ítems</div>
            <ItemsEditor items={items} setItems={setItems} />
            <div className="ap-total-linea">Total (con envío): <strong>${total.toLocaleString('es-UY')}</strong></div>

            <div className="ap-sec">Facturación (opcional)</div>
            <div className="ap-grid2">
              <label className="ap-field"><b>Razón social</b>
                <input value={f.facturacion_razon_social} onChange={e => set('facturacion_razon_social', e.target.value)} /></label>
              <label className="ap-field"><b>RUT</b>
                <input value={f.facturacion_rut} onChange={e => set('facturacion_rut', e.target.value)} placeholder="12 dígitos → e-Factura" /></label>
              <label className="ap-field"><b>Email facturación</b>
                <input value={f.facturacion_email} onChange={e => set('facturacion_email', e.target.value)} /></label>
            </div>

            <div className="ap-sec">Pago</div>
            <label className="ap-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={f.marcar_pagado} onChange={e => set('marcar_pagado', e.target.checked)} style={{ margin: 0 }} />
              <span><b>Ya está pagado</b> — lo crea directo como pagado (genera la entrega)</span>
            </label>
            {f.marcar_pagado && (
              <div className="ap-grid2">
                <label className="ap-field"><b>Método</b>
                  <select value={f.metodo_pago} onChange={e => set('metodo_pago', e.target.value)}>
                    <option value="">—</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="mercadopago">MercadoPago</option>
                    <option value="tarjeta_credito">Tarjeta crédito</option>
                    <option value="tarjeta_debito">Tarjeta débito</option>
                    <option value="abitab">Abitab</option>
                    <option value="redpagos">Redpagos</option>
                  </select></label>
                <label className="ap-field"><b>Referencia</b>
                  <input value={f.pago_referencia} onChange={e => set('pago_referencia', e.target.value)} /></label>
              </div>
            )}

            {err && <div className="ap-err">{err}</div>}
            <div className="ap-modal-foot">
              <button className="ap-btn-ghost" onClick={onClose}>Cancelar</button>
              <button className="ap-btn-ok" onClick={crear} disabled={busy}>{busy ? 'Creando…' : 'Crear pedido'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const styles = `
.page h1 { margin-top: 12px }
.muted { color: #64748b; font-size: 13px; margin-bottom: 16px }
.ap-head-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap }
.ap-btn-nuevo { background: linear-gradient(135deg, #1A3461, #00A8B5); color: #fff; border: 0; padding: 11px 20px; border-radius: 999px; font-size: 14px; font-weight: 800; cursor: pointer; font-family: inherit; white-space: nowrap; margin-top: 12px }

.ap-filtros { display: flex; gap: 6px; flex-wrap: wrap; margin: 16px 0 24px }
.ap-filtro { padding: 8px 14px; background: #f1f5f9; border: 1.5px solid transparent; border-radius: 999px; font-size: 13px; font-weight: 700; color: #1a3461; cursor: pointer; font-family: inherit; transition: all .15s }
.ap-filtro:hover { background: #e2e8f0 }
.ap-filtro.is-active { background: #1a3461; color: #fff }
.ap-filtro em { font-style: normal; font-size: 11px; opacity: .65; margin-left: 6px; font-weight: 700 }
.ap-filtro.is-active em { color: #00A8B5; opacity: 1 }
.ap-filtros-canal { margin-top: -12px; margin-bottom: 16px }
.ap-filtro-canal { background: transparent; border: 1px solid #e2e8f0; padding: 6px 12px; font-size: 12px }
.ap-filtro-canal.is-active { background: #00A8B5; color: #fff; border-color: #00A8B5 }

.ap-search {
  width: 100%; padding: 12px 16px; margin-bottom: 12px;
  border: 1.5px solid #e2e8f0; border-radius: 12px; font-size: 14px;
  font-family: inherit; color: #1a3461; box-sizing: border-box;
}
.ap-search:focus { outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.1) }

.ap-list-info {
  font-size: 12.5px; color: #64748b; margin: 8px 0 12px;
  display: flex; align-items: center; gap: 12px;
}
.ap-list-info strong { color: #1a3461 }
.ap-link {
  background: transparent; border: 0; color: #DC2626; cursor: pointer;
  font-family: inherit; font-size: 12px; font-weight: 700; padding: 2px 6px;
}
.ap-link:hover { text-decoration: underline }

.ap-loading, .ap-empty { text-align: center; padding: 60px 20px; color: #64748b }
.ap-empty h3 { color: #1a3461; margin: 12px 0 6px }

.ap-list { display: flex; flex-direction: column; gap: 10px }
.ap-pedido { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 2px 8px rgba(26,52,97,.04); overflow: hidden; transition: box-shadow .15s, border-color .15s }
.ap-pedido.is-collapsed { padding: 0 }
.ap-pedido.is-open { padding: 18px 20px }
.ap-pedido.is-collapsed:hover { border-color: #cbd5e1; box-shadow: 0 4px 12px rgba(26,52,97,.08) }

/* Highlight animado cuando se navega con ?highlight= */
@keyframes ap-pulse-highlight {
  0%   { border-color: #F59E0B; box-shadow: 0 0 0 4px rgba(245,158,11,.35) }
  60%  { border-color: #F59E0B; box-shadow: 0 0 0 8px rgba(245,158,11,.15) }
  100% { border-color: #F59E0B; box-shadow: 0 0 0 0 rgba(245,158,11,0) }
}
.ap-pedido.is-highlighted { border-color: #F59E0B !important; animation: ap-pulse-highlight 1.4s ease-out 2 }

.ap-pedido-head {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 12px 16px;
}
.ap-pedido.is-open .ap-pedido-head { margin-bottom: 14px; padding: 0 }
.ap-pedido.is-collapsed:hover .ap-pedido-head { background: #f8fafc }
.ap-pedido-head-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; flex-wrap: wrap }
.ap-expand-caret { color: #94a3b8; font-size: 12px; font-weight: 800; width: 10px }
.ap-pedido-num { font-size: 13px; font-weight: 800; color: #00a8b5; letter-spacing: .08em; font-family: ui-monospace, monospace }
.ap-pedido-cliente-compact { font-size: 14px; font-weight: 700; color: #1a3461; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
.ap-pedido-total-compact { font-size: 14px; font-weight: 800; color: #1a3461; margin-left: auto; font-variant-numeric: tabular-nums }
.ap-pedido.is-open .ap-pedido-total-compact { display: none }
.ap-pedido.is-open .ap-pedido-cliente-compact { display: none }
.ap-pedido-canal { font-size: 10.5px; color: #64748b; background: #f1f5f9; padding: 2px 8px; border-radius: 6px; font-weight: 700 }
.ap-pedido.is-open .ap-pedido-canal { display: none }
.ap-pill { padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase }

.ap-pedido-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 14px }
@media (max-width: 800px) { .ap-pedido-grid { grid-template-columns: repeat(2, 1fr) } }

.ap-copy { background: transparent; border: 0; cursor: pointer; color: #94a3b8; font-size: 12px; margin-left: 6px; padding: 0 }
.ap-copy:hover { color: #00a8b5 }

.ap-expand { background: transparent; border: 0; padding: 0; font: inherit; color: #00a8b5; cursor: pointer; text-align: left; text-decoration: underline; text-decoration-style: dotted; text-underline-offset: 2px }
.ap-expand:hover { color: #1A3461 }

.ap-items { margin: -4px 0 14px; padding: 12px 14px; background: #F8F6F1; border: 1px solid #E2E8F0; border-radius: 10px }
.ap-items-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px }
.ap-items-tabla th { text-align: left; padding: 6px 8px; font-size: 10.5px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #E2E8F0 }
.ap-items-tabla td { padding: 7px 8px; border-bottom: 1px dashed #EDE9DF; color: #334155 }
.ap-items-tabla tr:last-child td { border-bottom: 0 }
.ap-items-sku { font-family: ui-monospace, monospace; font-size: 11px; color: #94A3B8; font-weight: 700 }
.ap-items-mono { font-size: 11px; color: #64748b }
@media (max-width: 500px) { .ap-pedido-grid { grid-template-columns: 1fr } }

.ap-factura { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 10px 12px; border-radius: 10px; margin-bottom: 12px; font-size: 12.5px; color: #1a3461; line-height: 1.4 }
.ap-factura.is-empresa { background: #eff6ff; border: 1px solid #bfdbfe }
.ap-factura.is-cf { background: #f0fdf4; border: 1px solid #bbf7d0 }
.ap-factura-icon { font-size: 16px; line-height: 1 }
.ap-factura-tag { font-size: 10.5px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: .06em }
.ap-factura-pill { font-size: 10.5px; font-weight: 800; padding: 3px 9px; border-radius: 999px; text-transform: uppercase; letter-spacing: .04em }
.ap-factura-pill.emp { background: #1d4ed8; color: #fff }
.ap-factura-pill.cf { background: #15803d; color: #fff }
.ap-factura strong { font-weight: 800; color: #0f172a }
.ap-factura-rut { font-family: ui-monospace, monospace; color: #334155; font-weight: 700 }
.ap-factura-sub { color: #64748b; font-size: 11.5px; font-style: italic }

.ap-tag { font-size: 10px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 4px }
.ap-val { font-size: 14px; font-weight: 800; color: #1a3461; line-height: 1.3 }
.ap-sub { font-size: 12px; color: #475569; margin-top: 3px; line-height: 1.4 }
.ap-monto { font-size: 22px; letter-spacing: -.01em }
.ap-lib { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 6px; white-space: nowrap }
.ap-lib[data-estado="retenido"] { background: #fef3c7; color: #92400e }
.ap-lib[data-estado="liberado"] { background: #dcfce7; color: #15803d }

.ap-pedido-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; padding-top: 12px; border-top: 1px dashed #e2e8f0 }
.ap-btn-ok { background: linear-gradient(135deg, #15803d, #22c55e); color: #fff; border: 0; padding: 10px 18px; border-radius: 999px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.ap-btn-estado { background: #e0e7ff; color: #3730a3; border: 0; padding: 10px 16px; border-radius: 999px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.ap-btn-estado:hover { background: #c7d2fe }
.ap-btn-ghost { background: #f1f5f9; color: #1a3461; border: 0; padding: 10px 16px; border-radius: 999px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.ap-btn-ghost:hover { background: #e2e8f0 }
.ap-btn-link-danger { background: none; border: 0; color: #991b1b; font-size: 12px; font-weight: 600; cursor: pointer; text-decoration: underline; margin-left: auto; padding: 8px 0; font-family: inherit }
.ap-notas { margin-top: 12px; padding: 10px 14px; background: #fef3c7; border-radius: 10px; font-size: 13px; color: #92400e }
.ap-notas-details { margin-top: 12px; padding: 8px 12px; background: #fef3c7; border-radius: 10px; font-size: 12.5px; color: #78350f }
.ap-notas-details summary { cursor: pointer; font-weight: 700; padding: 2px 0; user-select: none; list-style: none }
.ap-notas-details summary::-webkit-details-marker { display: none }
.ap-notas-details summary::before { content: '▸ '; font-size: 11px; color: #92400e }
.ap-notas-details[open] summary::before { content: '▾ ' }
.ap-notas-content { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #fbbf24; white-space: pre-wrap; line-height: 1.5 }

/* Modal */
.ap-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px }
.ap-modal { background: #fff; border-radius: 16px; padding: 24px; max-width: 460px; width: 100%; max-height: 90vh; overflow-y: auto }
.ap-modal-wide { max-width: 640px }
.ap-modal h2 { font-size: 18px; font-weight: 900; color: #1a3461; margin: 0 0 14px; letter-spacing: -.01em }
.ap-modal-info { background: #f8fafc; padding: 12px 14px; border-radius: 10px; margin-bottom: 16px; font-size: 13px; line-height: 1.6; color: #1a3461 }
.ap-sec { font-size: 11px; font-weight: 800; color: #00a8b5; text-transform: uppercase; letter-spacing: .08em; margin: 18px 0 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px }
.ap-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
@media (max-width: 520px) { .ap-grid2 { grid-template-columns: 1fr } }
.ap-field { display: block; margin-bottom: 14px }
.ap-info-box { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; padding: 10px 12px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; line-height: 1.5 }
.ap-field b { display: block; font-size: 11px; font-weight: 800; color: #1a3461; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px }
.ap-field input, .ap-field textarea, .ap-field select { width: 100%; box-sizing: border-box; padding: 10px 12px; font-size: 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-family: inherit }
.ap-err { background: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; padding: 10px 14px; border-radius: 10px; font-size: 13px; margin-top: 10px }
.ap-modal-foot { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px }
.ap-btn-danger { background: #dc2626; color: #fff; border: 0; padding: 10px 18px; border-radius: 999px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.ap-total-preview { font-size: 20px; font-weight: 800; color: #1a3461; padding: 6px 0 }
.ap-total-linea { text-align: right; font-size: 14px; color: #475569; margin: 8px 0 4px }
.ap-total-linea strong { color: #1a3461; font-size: 17px }

/* Items editor */
.ap-items-editor { margin-bottom: 8px }
.ap-buscador { position: relative; margin-bottom: 10px }
.ap-buscador input { width: 100%; box-sizing: border-box; padding: 10px 12px; font-size: 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-family: inherit }
.ap-buscador-list { position: absolute; top: 100%; left: 0; right: 0; z-index: 20; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; box-shadow: 0 8px 24px rgba(15,31,61,.14); max-height: 260px; overflow-y: auto; margin-top: 4px }
.ap-buscador-item { display: flex; justify-content: space-between; gap: 10px; align-items: center; width: 100%; text-align: left; background: transparent; border: 0; border-bottom: 1px solid #f1f5f9; padding: 9px 12px; font-size: 12.5px; cursor: pointer; font-family: inherit; color: #334155 }
.ap-buscador-item:hover { background: #f0fdff }
.ap-buscador-item code { font-family: ui-monospace, monospace; font-size: 11px; color: #94a3b8; font-weight: 700 }
.ap-buscador-item em { color: #64748b; font-style: normal; font-size: 11px }
.ap-buscador-precio { white-space: nowrap; color: #00a8b5; font-weight: 700; font-size: 11.5px }
.ap-items-vacio { text-align: center; padding: 16px; color: #94a3b8; font-size: 13px; background: #f8fafc; border-radius: 10px }
.ap-items-edit td { vertical-align: middle }
.ap-mini-input { width: 100%; box-sizing: border-box; padding: 6px 8px; font-size: 13px; border: 1.5px solid #e2e8f0; border-radius: 8px; text-align: right; font-family: inherit }
.ap-quitar { background: #fee2e2; color: #991b1b; border: 0; width: 24px; height: 24px; border-radius: 6px; cursor: pointer; font-weight: 800; font-size: 15px; line-height: 1 }
.ap-quitar:hover { background: #fecaca }
`
